/**
 * PrescriptionBlock - Bloco de receita individual com nome e medicamentos
 * Permite criar/editar uma receita com nome personalizado e múltiplas vias
 */

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { X, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import MedicationGroupByRoute, { createEmptyMedication } from './MedicationGroupByRoute';

export default function PrescriptionBlock({
  prescription,
  index,
  onUpdate,
  onRemove,
  canRemove,
  collapsed = false,
  onToggleCollapse
}) {
  // Conta total de medicamentos válidos na receita
  const medicationCount = prescription.medicationGroups?.reduce(
    (count, group) => count + group.medicamentos.filter(m => m.name?.trim()).length,
    0
  ) || 0;

  return (
    <div className="border-2 border-primary/20 rounded-xl overflow-hidden bg-white shadow-sm" data-testid={`prescription-block-${index}`}>
      {/* Header do bloco de receita */}
      <div 
        className="bg-primary/5 p-4 flex items-center justify-between cursor-pointer hover:bg-primary/10 transition-all"
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <FileText className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-800">
              {prescription.name || `Receita ${index + 1}`}
            </h4>
            <p className="text-xs text-gray-500">
              {medicationCount} medicamento(s) • {prescription.medicationGroups?.length || 1} via(s)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canRemove && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
              data-testid={`remove-prescription-${index}`}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
          {collapsed ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronUp className="w-5 h-5 text-gray-500" />
          )}
        </div>
      </div>

      {/* Conteúdo do bloco (colapsável) */}
      {!collapsed && (
        <div className="p-4 space-y-4">
          {/* Campo Nome da Receita */}
          <div>
            <Label className="text-sm font-medium text-gray-600 mb-2 block">
              Nome da Receita (opcional)
            </Label>
            <Input
              value={prescription.name || ''}
              onChange={(e) => onUpdate({ ...prescription, name: e.target.value })}
              placeholder="Ex: Receita Hipertensão, Medicação Contínua, etc."
              className="h-10"
              data-testid={`prescription-name-${index}`}
            />
            <p className="text-xs text-gray-400 mt-1">
              Um nome ajuda a identificar esta receita na lista e na impressão
            </p>
          </div>

          {/* Medicamentos por via de administração */}
          <MedicationGroupByRoute
            groups={prescription.medicationGroups || [{ via: 'ORAL', medicamentos: [createEmptyMedication()] }]}
            onChange={(groups) => onUpdate({ ...prescription, medicationGroups: groups })}
            dataTestIdPrefix={`rx-${index}-`}
          />
        </div>
      )}
    </div>
  );
}

// Função auxiliar para criar uma receita vazia
export function createEmptyPrescription() {
  return {
    name: '',
    medicationGroups: [
      {
        via: 'ORAL',
        medicamentos: [createEmptyMedication()]
      }
    ]
  };
}
